import type { StaticImageData } from "next/image";
import dr_of_naturopathic_medicine from "@images/dr_of_naturopathic_medicine.png";
import dr_of_traditional_islamic_medicine from "@images/dr_of_traditional_islamic_med.png";
import dr_of_medicine_acupuncture from "@images/Doctor_of_Medicine_in_Acupuncture.png";
import diploma_in_naturopathy from "@images/Diploma_in_Naturopathy.png";
import diploma_in_clinical_acupuncture from "@images/diploma_in_clinical_acupunture.png";
import certificate_in_herbal_medicine_treatment_for_chronic_diseases from "@images/certificate_in_herbal_medicine_treatment_for_chronic_diseases.png";
import certificate_in_tcm from "@images/certificate_in_tcm_diagnosis.png";
import Certificate_in_Weight_Management from "@images/Certificate_in_Weight_Management.png";
import Certificate_in_Healthy_Living from "@images/Certificate_in_Healthy_Living.png";
import cet_in_herbal_med_protocol_for_autism from "@images/cet_in_herbal_med_protocol_for_autism.png";
import cert_in_diet_and_nutrition_therapy_in_cancer from "@images/cert_in_diet_and_nutrition_therapy_in_cancer.png";

export interface Programme {
  slug: string;
  title: string;
  image: StaticImageData;
  category: "doctorate" | "diploma" | "certificate";
  seo: {
    metaTitle: string;
    metaDescription: string;
  };
  overview: {
    description: string;
    whoIsItFor: string[];
    duration: {
      length: number;
      unit: string;
      note: string;
    };
    tuitionFee: {
      information: string;
    };
  };
  curriculum: {
    composition: string;
    courses: string[];
    methodsOfInstruction: string[];
  };
  admissionRequirements: {
    criteria: string[];
    reviewTimeline: string;
  };
  recognition: {
    accreditation: { body: string; status: string }[];
    note: string;
  };
  learningOutcomes: string[];
  careerPaths: { title: string; description: string }[];
  callToAction: {
    primary: string;
    secondary: string;
  };
}

export const programmes: Programme[] = [
  {
    slug: "doctor-of-medicine-acupuncture",
    title: "Doctor of Medicine in Acupuncture",
    image: dr_of_medicine_acupuncture,
    category: "doctorate",
    seo: {
      metaTitle: "Doctor of Medicine in Acupuncture | OICNM",
      metaDescription:
        "Enroll in the Doctor of Medicine in Acupuncture at OICNM. Gain advanced training in Traditional Chinese Medicine, clinical practice, and integrative healthcare in a comprehensive 3-year programme.",
    },
    overview: {
      description:
        "The Doctor of Medicine in Acupuncture programme at OICNM is a comprehensive and advanced degree programme designed to provide students with a deep understanding of traditional acupuncture practices, integrative healthcare, and holistic wellness. Through a combination of theoretical knowledge, practical training, and clinical experience, this programme equips students with the skills and expertise needed to excel as qualified acupuncturists in the field of natural medicine.",
      whoIsItFor: [
        "Aspiring acupuncturists",
        "Healthcare professionals seeking to integrate acupuncture into existing practices",
        "Individuals passionate about natural healing and holistic healthcare",
        "Practitioners seeking to expand their clinical skills",
      ],
      duration: {
        length: 3,
        unit: "years",
        note: "Typically completed within 3 years, including both coursework and clinical training.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "The programme is composed of a rigorous curriculum that includes theoretical courses, practical hands-on training, and clinical rotations, ensuring a comprehensive learning experience through classroom instruction and laboratory sessions.",
      courses: [
        "Fundamentals of Traditional Chinese Medicine",
        "Acupuncture Techniques and Point Location",
        "Herbal Medicine and Formulas",
        "Biomedical Foundations for Acupuncturists",
        "Acupuncture Research and Evidence-Based Practice",
        "Clinical Assessment and Diagnosis",
        "Integrative Approaches to Healthcare",
      ],
      methodsOfInstruction: [
        "Lectures",
        "Group discussions",
        "Laboratory sessions",
        "Practical demonstrations",
        "Clinical rotations",
        "Supervised practice",
      ],
    },
    admissionRequirements: {
      criteria: [
        "A bachelor's degree from an accredited institution",
        "Submission of completed application form and required documents",
        "Interview with the Admissions Committee",
        "Letters of recommendation",
        "Demonstrated interest in natural medicine and acupuncture",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "Nigeria Association of Clinical Acupuncturists (NACA)",
          status: "Recognised",
        },
      ],
      note: "Check our Accreditation section for more information.",
    },
    learningOutcomes: [
      "Demonstrate a comprehensive understanding of traditional acupuncture principles and techniques",
      "Apply acupuncture and holistic healthcare concepts to promote wellness and manage health conditions",
      "Conduct thorough patient assessments and develop personalized treatment plans",
      "Integrate acupuncture practices with biomedical knowledge for holistic patient care",
      "Participate effectively in clinical settings and engage in evidence-based practice",
      "Communicate professionally and ethically with patients and healthcare teams",
    ],
    careerPaths: [
      {
        title: "Licensed Acupuncturist",
        description:
          "Provide acupuncture treatments to clients in private practices, wellness centres, and clinics to manage pain and alleviate stress.",
      },
      {
        title: "Integrative Healthcare Practitioner",
        description:
          "Work alongside medical doctors, chiropractors, and naturopathic doctors to provide complementary healthcare solutions.",
      },
      {
        title: "Clinical Supervisor or Educator",
        description:
          "Transition into roles as supervisors or educators within acupuncture schools, training the next generation of practitioners.",
      },
      {
        title: "Research and Academia",
        description:
          "Pursue research roles within academic institutions or research centres to study the effectiveness of acupuncture treatments.",
      },
      {
        title: "Health and Wellness Consultant",
        description:
          "Offer advice on holistic healthcare, providing personalised wellness plans that incorporate acupuncture and nutrition.",
      },
      {
        title: "Corporate Wellness Programmes",
        description:
          "Collaborate with corporations to design and implement wellness programmes, including stress reduction through acupuncture.",
      },
      {
        title: "Health Spa and Resorts",
        description:
          "Provide acupuncture treatments in luxury resorts and wellness spas as part of a comprehensive wellness experience.",
      },
      {
        title: "Non-profit and Community Health Organisations",
        description:
          "Offer services in community clinics and outreach programmes to provide healthcare access to underserved populations.",
      },
      {
        title: "Entrepreneurship",
        description:
          "Open private clinics or develop and market natural health product lines, such as herbal remedies or wellness tools.",
      },
      {
        title: "Global Health Initiatives",
        description:
          "Collaborate with international organizations to provide acupuncture treatments in diverse global regions.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "doctor-of-naturopathic-medicine",
    title: "Doctor of Naturopathic Medicine",
    image: dr_of_naturopathic_medicine,
    category: "doctorate",
    seo: {
      metaTitle: "Doctor of Naturopathic Medicine | OICNM",
      metaDescription:
        "Pursue your Doctor of Naturopathic Medicine at OICNM. Rigorous 4-year program with integrated body-systems curriculum and mandatory clinical postings.",
    },
    overview: {
      description:
        "A rigorous, professionally oriented medical training program designed to produce competent, ethical, and confident naturopathic physicians through an integrated body-systems approach.",
      whoIsItFor: [
        "Diploma in Naturopathy holders (CGPA 2.5+)",
        "University graduates in Health, Life Sciences, or Nursing",
        "Medical Doctors and Licensed Healthcare Professionals",
        "PhD and Master's holders in health-related disciplines",
      ],
      duration: {
        length: 4,
        unit: "years",
        note: "Full-time study with mandatory 300-hour clinical postings in Lagos every semester.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    admissionRequirements: {
      criteria: [
        "Diploma in Naturopathy (Min CGPA 2.5) for Level 2 entry",
        "University Degree in Sciences/Health (Min CGPA 2.5) for Level 1 entry",
        "Advanced Professionals (MD/PhD) may enter Level 2 via Exam & Interview",
        "Science courses older than 7 years require individual assessment",
        "Proficiency in written and spoken English",
        "Laptop and reliable internet access required",
      ],
      reviewTimeline:
        "Spring and Fall intakes twice yearly. Final decisions made by the OICNM Academic Board.",
    },
    curriculum: {
      composition:
        "Integrated body-systems approach combining core medical sciences (Anatomy, Physiology, Pathology) with naturopathic therapeutics.",
      courses: [
        "Anatomy & Physiology",
        "Pathology & Pharmacology",
        "Clinical Nutrition",
        "Botanical Medicine",
        "Naturopathic Philosophy & Principles",
        "Diagnostic Reasoning",
        "Integrative Treatment Planning",
      ],
      methodsOfInstruction: [
        "Full-time in-person study",
        "Mandatory clinical postings in Lagos",
        "300-hour clinical rotations per semester",
        "Case-based learning",
        "Limited didactic coursework available online",
      ],
    },
    learningOutcomes: [
      "Mastery of human health and disease processes",
      "Competence in diagnostic reasoning and clinical skills",
      "Ability to design ethical, integrative treatment plans",
      "Preparedness for professional naturopathic physician practice",
    ],
    careerPaths: [
      {
        title: "Naturopathic Physician",
        description:
          "Clinical practice in private or integrative healthcare settings.",
      },
    ],
    recognition: {
      accreditation: [
        {
          body: "OICNM Academic Board",
          status: "Accredited",
        },
      ],
      note: "Meeting minimum requirements does not guarantee admission; bridging courses may be required.",
    },
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "doctor-of-traditional-islamic-medicine",
    title: "Doctor of Traditional Islamic Medicine",
    image: dr_of_traditional_islamic_medicine,
    category: "doctorate",
    seo: {
      metaTitle: "Doctor of Traditional Islamic Medicine (DTIM) | OICNM",
      metaDescription:
        "Explore the intersection of traditional Islamic healing and modern healthcare. A 3-year program covering Prophetic medicine, Hijaamah, Ruqyah, and holistic wellness.",
    },
    overview: {
      description:
        "A comprehensive exploration of the intersection between traditional Islamic healing practices and modern healthcare. The program integrates ancient wisdom with contemporary approaches, equipping students with a deep understanding of natural remedies, prophetic medicine, and holistic wellness.",
      whoIsItFor: [
        "Muslim applicants from all backgrounds",
        "Aspiring practitioners of Islamic medicine",
        "Healthcare professionals seeking to incorporate holistic approaches",
        "Individuals committed to promoting wellness in alignment with Islamic principles",
      ],
      duration: {
        length: 3,
        unit: "years",
        note: "Typically completed within 3 years, encompassing coursework, practical workshops, and clinical experience.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "A comprehensive blend of theoretical studies, experiential learning, and hands-on clinical practice, including classroom instruction and practical workshops.",
      courses: [
        "Fundamentals of Prophetic Medicine",
        "Herbal Remedies in Islamic Tradition",
        "Islamic Nutrition and Dietary Practices",
        "Healing through the Qur'an and Sunnah",
        "Hijaamah (Cupping) Therapy",
        "Ruqyah",
        "Holistic Approaches to Wellness in Islamic Culture",
        "Integrating Modern and Traditional Islamic Medicine",
      ],
      methodsOfInstruction: [
        "Lectures and discussions",
        "Practical workshops and demonstrations",
        "Supervised clinical rotations",
        "Hands-on clinical practice",
        "Application of traditional principles in clinical settings",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to Muslim applicants from all backgrounds",
        "Minimum requirement: SSCE / O-Level or equivalent",

        "Interview with the Admissions Committee",
        "Submission of completed application form and required documents",
        "Genuine interest in traditional Islamic medicine and holistic health",
      ],
      reviewTimeline:
        "10 working/business days for review and screening after which admission notification will be sent.",
    },
    recognition: {
      accreditation: [
        {
          body: "Lagos State Traditional Medicine Board (LSTMB) under the Lagos State Ministry of Health",
          status: "Recognised",
        },
      ],
      note: "All admissions are subject to document verification. OICNM reserves the right to recommend bridging modules where necessary.",
    },
    learningOutcomes: [
      "Demonstrate a profound understanding of traditional Islamic healing and its relevance to holistic health",
      "Apply traditional Islamic medicine principles to support wellness and address health challenges",
      "Design personalized healing protocols based on Islamic traditions and contemporary approaches",
      "Provide compassionate and culturally sensitive patient care guided by Islamic values",
      "Collaborate with other healthcare professionals to offer integrative solutions",
      "Contribute to research and advocacy in the field of traditional Islamic medicine",
      "Communicate effectively with patients and communities about Islamic health practices",
    ],
    careerPaths: [
      {
        title: "Traditional Islamic Medicine Practitioner",
        description:
          "Establish a private practice providing holistic consultations, nutrition, and prophetic healing.",
      },
      {
        title: "Holistic Healthcare Clinics",
        description:
          "Collaborate with wellness centres to offer programs combining conventional and Islamic approaches.",
      },
      {
        title: "Islamic Health Education and Advocacy",
        description:
          "Educate communities through seminars and workshops on traditional Islamic medicine.",
      },
      {
        title: "Consultant for Islamic Nutrition and Dietetics",
        description:
          "Guide individuals on healthful eating in alignment with Islamic dietary laws and traditions.",
      },
      {
        title: "Islamic Hospice and Palliative Care",
        description:
          "Provide compassionate end-of-life support integrating traditional healing for spiritual well-being.",
      },
      {
        title: "Research and Academia",
        description:
          "Conduct studies, publish findings, and teach traditional Islamic medicine in academic settings.",
      },
      {
        title: "Health and Wellness Writing",
        description:
          "Author articles, books, and blogs on holistic healing and wellness from an Islamic perspective.",
      },
      {
        title: "Community Health and Outreach",
        description:
          "Work with nonprofits to provide health education rooted in traditional Islamic medicine.",
      },
      {
        title: "Islamic Spa and Wellness Retreats",
        description:
          "Provide therapies and lifestyle guidance in resorts offering Islamic-inspired wellness.",
      },
      {
        title: "Entrepreneurship",
        description:
          "Establish wellness centres, clinics, or online platforms offering Islamic healing products and services.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },

  {
    slug: "diploma-in-naturopathy",
    title: "Professional Diploma in Naturopathy",
    image: diploma_in_naturopathy,
    category: "diploma",
    seo: {
      metaTitle: "Diploma in Naturopathy | OICNM",
      metaDescription:
        "Earn your Professional Diploma in Naturopathy at OICNM. A 2-year foundational program in holistic wellness, herbal remedies, and lifestyle counselling.",
    },
    overview: {
      description:
        "A comprehensive introduction to the principles and practices of natural medicine. Rooted in holistic wellness and alternative healing modalities, this programme equips students with a foundational understanding of naturopathic approaches to support individuals on their journey to well-being.",
      whoIsItFor: [
        "Individuals seeking a foundational education in naturopathic principles",
        "Aspiring healthcare professionals interested in natural medicine",
        "School leavers with a senior school certificate (high school)",
        "Individuals intrigued by natural healing and holistic health",
      ],
      duration: {
        length: 2,
        unit: "years",
        note: "Typically completed within 2 years, including coursework and practical training.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "A balanced curriculum comprising theoretical knowledge and practical application, including classroom instruction, hands-on training, and experiential learning.",
      courses: [
        "Introduction to Naturopathic Principles",
        "Herbal Remedies and Botanical Therapies",
        "Nutrition and Holistic Dietary Practices",
        "Lifestyle Counselling and Stress Management",
        "Basics of Homeopathy and Natural Healing",
        "Integrative Approaches to Wellness",
      ],
      methodsOfInstruction: [
        "Lectures and discussions",
        "Practical workshops",
        "Hands-on exercises",
        "Experiential learning",
        "Case studies",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form and required documents",
        "Interest in naturopathy and holistic health",
        "Letters of recommendation",
        "Interview with the Admissions Committee",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent.",
    },
    recognition: {
      accreditation: [
        {
          body: "Lagos State Traditional Medicine Board (LSTMB)",
          status: "Recognised for Licensure",
        },
      ],
      note: "Graduates can apply for licensure through regulatory bodies in their region, state, or country (such as LSTMB in Lagos state).",
    },
    learningOutcomes: [
      "Understand the fundamental principles of naturopathy and holistic health",
      "Recognize the benefits of natural remedies, including herbal medicine and nutritional approaches",
      "Promote wellness through lifestyle adjustments and stress management techniques",
      "Provide basic guidance on nutrition, dietary practices, and holistic living",
      "Advocate for the integration of natural medicine in holistic healthcare",
      "Communicate effectively about naturopathic concepts with clients and peers",
    ],
    careerPaths: [
      {
        title: "Naturopathic Practitioner",
        description:
          "Offer foundational naturopathic consultations and complementary health practices.",
      },
      {
        title: "Wellness Coach",
        description:
          "Guide individuals on lifestyle adjustments and stress management for optimal well-being.",
      },
      {
        title: "Health Product Consultant",
        description:
          "Advise on natural remedies, herbal supplements, and nutritional products.",
      },
      {
        title: "Community Health Educator",
        description:
          "Advocate for holistic health and natural medicine integration within community settings.",
      },
      {
        title: "Entrepreneur",
        description:
          "Establish a wellness-focused business or natural health consultancy.",
      },
      {
        title: "Further Studies",
        description:
          "Progress to advanced doctorate programs such as the Doctor of Naturopathic Medicine (DNM).",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },

  // {
  //   slug: "diploma-in-herbal-medicine",
  //   title: "Professional Diploma in Herbal Medicine",
  //   image: "/placeholder.svg",
  //   category: "diploma",
  //   seo: {
  //     metaTitle: "Diploma in Herbal Medicine | OICNM",
  //     metaDescription:
  //       "Study Herbal Medicine at OICNM with a 2-year professional diploma programme.",
  //   },
  //   overview: {
  //     description:
  //       "A comprehensive 2-year diploma covering medicinal botany, herbal formulation, pharmacognosy, and clinical herbal practice. Students learn to prepare and prescribe herbal remedies safely.",
  //     whoIsItFor: [
  //       "Aspiring herbalists",
  //       "Traditional medicine practitioners",
  //       "Pharmacists interested in herbal alternatives",
  //       "Health enthusiasts passionate about plant medicine",
  //     ],
  //     duration: {
  //       length: 2,
  //       unit: "years",
  //       note: "Includes field work and clinical practice",
  //     },
  //     tuitionFee: { information: "Contact the Admissions Office for fees." },
  //   },
  //   curriculum: {
  //     composition:
  //       "A balanced curriculum of botanical science, herbal pharmacology, and supervised clinical practice.",
  //     courses: [
  //       "Medicinal Botany",
  //       "Herbal Pharmacognosy",
  //       "Herbal Formulation & Dispensing",
  //       "Plant Identification & Field Work",
  //       "Traditional African Herbal Medicine",
  //       "Safety & Toxicology of Herbs",
  //       "Clinical Herbal Practice",
  //     ],
  //     methodsOfInstruction: [
  //       "Lectures",
  //       "Field trips",
  //       "Laboratory work",
  //       "Clinical practice",
  //       "Research projects",
  //       "Workshops",
  //     ],
  //   },
  //   admissionRequirements: {
  //     criteria: [
  //       "SSCE/O-Level certificate or equivalent",
  //       "Completed application form",
  //       "Interest in plant-based medicine",
  //       "Passport photographs",
  //     ],
  //     reviewTimeline: "Applications processed within 7 business days.",
  //   },
  //   recognition: {
  //     accreditation: [
  //       { body: "OICNM Certification Board", status: "Recognised" },
  //     ],
  //     note: "Graduates receive an OICNM Professional Diploma in Herbal Medicine.",
  //   },
  //   learningOutcomes: [
  //     "Identify and classify medicinal plants",
  //     "Formulate and dispense herbal remedies safely",
  //     "Understand herbal pharmacology and contraindications",
  //     "Apply traditional and modern herbal practices",
  //     "Conduct patient consultations for herbal treatment",
  //     "Practise ethically and maintain professional standards",
  //   ],
  //   careerPaths: [
  //     {
  //       title: "Herbal Medicine Practitioner",
  //       description: "Provide herbal consultations and treatments.",
  //     },
  //     {
  //       title: "Herbal Product Developer",
  //       description: "Create and manufacture herbal health products.",
  //     },
  //     {
  //       title: "Botanical Researcher",
  //       description: "Research medicinal properties of plants.",
  //     },
  //     {
  //       title: "Traditional Medicine Consultant",
  //       description:
  //         "Advise on integrating traditional remedies in healthcare.",
  //     },
  //     {
  //       title: "Apothecary Owner",
  //       description: "Operate a herbal dispensary or apothecary.",
  //     },
  //     {
  //       title: "Agricultural Herbalist",
  //       description: "Cultivate medicinal plants commercially.",
  //     },
  //     {
  //       title: "Health Educator",
  //       description: "Teach communities about safe herbal medicine use.",
  //     },
  //     {
  //       title: "Spa & Wellness Practitioner",
  //       description: "Offer herbal therapies in wellness settings.",
  //     },
  //     {
  //       title: "Export Consultant",
  //       description: "Advise on international trade of medicinal herbs.",
  //     },
  //     {
  //       title: "Further Studies",
  //       description: "Progress to advanced programmes at OICNM.",
  //     },
  //   ],
  //   callToAction: {
  //     primary: "Apply Now",
  //     secondary: "Request More Information",
  //   },
  // },

  {
    slug: "diploma-in-clinical-acupuncture",
    title: "Professional Diploma in Clinical Acupuncture",
    image: diploma_in_clinical_acupuncture,
    category: "diploma",
    seo: {
      metaTitle: "Diploma in Clinical Acupuncture | OICNM",
      metaDescription:
        "Earn your Professional Diploma in Clinical Acupuncture at OICNM. A practical 18-month program blending TCM principles with modern scientific understanding.",
    },
    overview: {
      description:
        "A comprehensive and practical diploma designed to equip students with the knowledge and skills needed to practice acupuncture as a standalone therapy or an integrative modality. This programme blends ancient traditional Chinese medicine principles with modern scientific understanding.",
      whoIsItFor: [
        "Aspiring acupuncturists",
        "Healthcare professionals looking to incorporate acupuncture into their practice",
        "Individuals interested in energy medicine and holistic healing",
        "Individuals seeking personal growth and wellness through acupuncture",
      ],
      duration: {
        length: 18,
        unit: "months",
        note: "Designed as a part-time programme.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "Structured to provide a comprehensive understanding of acupuncture theory, techniques, and practical applications, including hands-on clinical training and meridian pathways.",
      courses: [
        "Introduction to Acupuncture and Traditional Chinese Medicine (TCM)",
        "Acupuncture Points and Meridian Pathways",
        "Acupuncture Techniques and Needling Skills",
        "Acupuncture for Pain Management",
        "Acupuncture for Internal Medicine",
        "Acupuncture for Mental and Emotional Health",
        "Acupuncture for Women's Health",
        "Integrative Acupuncture Practice",
        "Acupuncture Practicum and Clinical Training",
      ],
      methodsOfInstruction: [
        "Classroom-based lectures",
        "Practical hands-on training",
        "Clinical observations",
        "Demonstrations",
        "Supervised needling practice",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "A strong interest in acupuncture and traditional Chinese medicine",
        "A statement of purpose detailing motivation",
        "Letters of recommendation from academic or professional references (if available)",
        "An interview with the Admissions Committee may be required",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Excellence in Acupuncture Education",
          status: "Recognised",
        },
      ],
      note: "The diploma confers certification as a clinical acupuncturist. Graduates should check local regulatory requirements for licensing.",
    },
    learningOutcomes: [
      "Proficiency in acupuncture needling techniques and point location",
      "Deep understanding of traditional Chinese medicine principles and meridian theory",
      "Competence in applying acupuncture for pain, internal medicine, and mental health",
      "Ability to create personalized acupuncture treatment plans",
      "Ethical practice and responsible use of acupuncture within healthcare settings",
    ],
    careerPaths: [
      {
        title: "Clinical Acupuncturist",
        description:
          "Practice acupuncture as a standalone or integrative therapy in clinical settings.",
      },
      {
        title: "Integrative Healthcare Provider",
        description:
          "Incorporate acupuncture techniques into existing medical or therapeutic practices.",
      },
      {
        title: "Pain Management Specialist",
        description:
          "Specialize in treating chronic and acute pain through targeted needle therapy.",
      },
      {
        title: "Wellness Consultant",
        description:
          "Advise on holistic healing and energy medicine within wellness centers.",
      },
      {
        title: "Acupuncture Entrepreneur",
        description:
          "Establish a private practice or mobile acupuncture service.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },

  {
    slug: "certificate-in-tcm-diagnosis",
    title: "Certificate in TCM Diagnosis",
    image: certificate_in_tcm,
    category: "certificate",
    seo: {
      metaTitle: "Certificate in TCM Diagnosis | OICNM",
      metaDescription:
        "Master Traditional Chinese Medicine diagnosis techniques at OICNM. 16-week intensive covering pulse, tongue, and pattern identification.",
    },
    overview: {
      description:
        "The TCM Diagnosis Programme at OICNM offers specialized training in Traditional Chinese Medicine (TCM) diagnosis techniques. Rooted in ancient wisdom and holistic healing, this programme equips students with the knowledge and skills to accurately assess and diagnose health imbalances using TCM principles and diagnostic methods.",
      whoIsItFor: [
        "Aspiring TCM practitioners",
        "Healthcare professionals seeking deeper assessment skills",
        "Individuals intrigued by the holistic approach of Traditional Chinese Medicine",
        "Individuals seeking a deeper understanding of TCM diagnosis",
      ],
      duration: {
        length: 16,
        unit: "weeks",
        note: "Encompassing coursework, practical training, and clinical experience.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on tuition fees and any available financial aid or scholarship opportunities.",
      },
    },
    curriculum: {
      composition:
        "The programme comprises an in-depth curriculum that covers TCM diagnostic methods and their application through classroom instruction, hands-on practice, and clinical observations.",
      courses: [
        "Foundations of Traditional Chinese Medicine",
        "Pulse Diagnosis and Interpretation",
        "Tongue Diagnosis and Analysis",
        "Observation of Facial Signs and Body Features",
        "Five Element Diagnosis and Constitutional Analysis",
        "Diagnostic Methods in TCM Pattern Identification",
        "Integrating TCM Diagnosis with Treatment Planning",
      ],

      methodsOfInstruction: [
        "Lectures",
        "Hands-on workshops",
        "Case studies",
        "Clinical practice",
        "Supervised diagnostic sessions",
        "Observations in clinical settings",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Possession of a bachelor's degree or equivalent in a relevant field",
        "Submission of a completed application form along with required documents",
        "Demonstrated interest in Traditional Chinese Medicine and diagnostic techniques",
        "Strong academic background with relevant coursework",
        "Letters of recommendation",
        "Admissions interview",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Licensed & Recognized Program",
          status: "Duly Licensed",
        },
      ],
      note: "The TCM Diagnosis Programme offered by OICNM is duly licenced and recognised.",
    },
    learningOutcomes: [
      "Understand the foundations of Traditional Chinese Medicine and its diagnostic principles",
      "Apply pulse diagnosis and interpret findings for health assessment",
      "Analyse tongue characteristics to determine health imbalances",
      "Recognize facial signs, body features, and other visual cues for diagnosis",
      "Utilise Five Element analysis to assess constitutional imbalances",
      "Identify TCM patterns and develop treatment plans based on diagnosis",
    ],
    careerPaths: [
      {
        title: "TCM Diagnostic Specialist",
        description:
          "Accurately assess and diagnose health imbalances using advanced TCM principles.",
      },
      {
        title: "Holistic Healthcare Practitioner",
        description:
          "Integrate TCM diagnostic methods into broader natural medicine practices.",
      },
      {
        title: "Clinical Diagnostic Aide",
        description:
          "Support healthcare teams with specialized pulse, tongue, and facial observations.",
      },
      {
        title: "Wellness Consultant",
        description:
          "Offer health assessments rooted in constitutional and Five Element analysis.",
      },
      {
        title: "Further Studies",
        description:
          "Progress to advanced diploma or doctorate programmes within OICNM.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "certificate-in-weight-management",
    title: "Certificate in Weight Management",
    image: Certificate_in_Weight_Management,
    category: "certificate",
    seo: {
      metaTitle: "Certificate in Weight Management | OICNM",
      metaDescription:
        "Master holistic weight loss and gain strategies in this intensive 4-week certificate program at OICNM.",
    },
    overview: {
      description:
        "The Weight Loss and Weight Gain Programme at OICNM offers comprehensive training in the principles and practices of healthy weight management. Rooted in holistic wellness, this program equips students with the skills to guide individuals towards maintaining desired weight goals through evidence-based, balanced approaches.",
      whoIsItFor: [
        "Aspiring nutrition professionals",
        "Healthcare practitioners seeking holistic tools",
        "Individuals passionate about promoting health through weight management",
        "Anyone seeking to help others achieve balanced weight goals",
      ],
      duration: {
        length: 4,
        unit: "weeks",
        note: "Encompasses both intensive coursework and practical training.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "A balanced curriculum covering both weight loss and weight gain strategies through classroom learning, case studies, and practical exercises to ensure a comprehensive understanding of metabolism and energy balance.",

      courses: [
        "Fundamentals of Weight Management",
        "Nutritional Strategies for Healthy Weight Loss",
        "Metabolism and Energy Balance",
        "Emotional Eating and Behavioural Change",
        "Nutritional Approaches for Healthy Weight Gain",
        "Physical Activity and Exercise in Weight Management",
        "Personalized Weight Management Plans",
      ],
      methodsOfInstruction: [
        "Lectures and group discussions",
        "Detailed case studies",
        "Practical exercises",
        "Scenario-based hands-on application",
        "Analysis of real-world weight management scenarios",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form and required documents",
        "Demonstrated interest in weight management and holistic health",
        "Letters of recommendation",
        "Interview with the Admissions Committee",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Licensed & Recognized Program",
          status: "Duly Licensed",
        },
      ],
      note: "Refer to the Accreditation section for detailed information on licensing and recognition.",
    },
    learningOutcomes: [
      "Understand the complex factors influencing weight loss and weight gain",
      "Develop personalized weight management plans based on individual metabolic needs",
      "Address emotional and behavioural factors affecting eating habits",
      "Incorporate evidence-based practices into holistic weight management strategies",
      "Guide clients in making sustainable dietary and lifestyle changes",
      "Promote the importance of balanced nutrition and physical activity",
    ],
    careerPaths: [
      {
        title: "Weight Management Coach",
        description:
          "Guide individuals through personalized weight loss or gain protocols using holistic methods.",
      },
      {
        title: "Nutrition & Wellness Consultant",
        description:
          "Advise clients on healthy dietary practices and lifestyle changes for long-term health.",
      },
      {
        title: "Fitness & Lifestyle Facilitator",
        description:
          "Incorporate specialized weight management strategies into fitness or healthcare environments.",
      },
      {
        title: "Corporate Wellness Coach",
        description:
          "Lead workplace weight management and healthy living initiatives.",
      },
      {
        title: "Private Practice Entrepreneur",
        description:
          "Establish a health coaching business focused on evidence-based weight management.",
      },
    ],
    callToAction: {
      primary: "Enroll Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "certificate-in-diet-nutrition-therapy-cancer",
    title: "Certificate in Diet and Nutrition Therapy in Cancer",
    image: cert_in_diet_and_nutrition_therapy_in_cancer,
    category: "certificate",
    seo: {
      metaTitle: "Certificate in Diet and Nutrition Therapy in Cancer | OICNM",
      metaDescription:
        "Specialized 4-week training in oncology nutrition. Learn to create personalized dietary plans for cancer treatment and recovery at OICNM.",
    },
    overview: {
      description:
        "The Diet & Nutrition Therapy in Cancer Programme at OICNM offers specialized training in using diet and nutrition as complementary therapies for individuals affected by cancer. Rooted in evidence-based practices, this programme equips students with the knowledge and skills to provide personalized dietary guidance that supports cancer patients' well-being during their treatment and recovery journey.",
      whoIsItFor: [
        "Aspiring nutrition professionals",
        "Healthcare practitioners",
        "Individuals seeking to provide informed dietary support to cancer patients",
        "Individuals passionate about holistic health and its application in cancer care",
      ],
      duration: {
        length: 4,
        unit: "weeks",
        note: "Typically completed within 4 weeks, encompassing coursework and practical training.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "The programme consists of a focused curriculum that combines theoretical knowledge, practical application, and clinical insights through classroom learning, case studies, and practical exercises.",

      courses: [
        "Basics of Oncology Nutrition",
        "Nutritional Considerations During Cancer Treatment",
        "Integrating Holistic Nutrition in Cancer Care",
        "Dietary Strategies for Managing Side Effects",
        "Antioxidants, Phytochemicals, and Immune Support",
        "Personalized Nutrition Plans for Cancer Patients",
        "Ethical and Cultural Considerations in Oncology Nutrition",
      ],
      methodsOfInstruction: [
        "Lectures",
        "Case studies",
        "Group discussions",
        "Practical exercises",
        "Mock patient scenarios",
        "Hands-on case analysis",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form along with required documents",
        "Strong academic background with relevant coursework",
        "Demonstrated interest in nutrition therapy and cancer care",
        "Letters of recommendation",
        "An interview with the Admissions Committee may be required",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Accredited & Licenced Program",
          status: "Duly Accredited",
        },
      ],
      note: "The Diet & Nutrition Therapy in Cancer Programme offered by OICNM is duly accredited and licenced.",
    },
    learningOutcomes: [
      "Understand the impact of nutrition on cancer treatment and recovery",
      "Create personalized nutrition plans tailored to individual cancer patients",
      "Address common side effects of cancer treatments through dietary strategies",
      "Incorporate evidence-based practices and research into nutrition therapy",
      "Collaborate effectively with oncology healthcare teams and patients",
      "Demonstrate cultural competence and ethical considerations in cancer nutrition",
    ],
    careerPaths: [
      {
        title: "Oncology Nutrition Consultant",
        description:
          "Provide specialized dietary guidance for patients undergoing cancer treatment and recovery.",
      },
      {
        title: "Holistic Cancer Care Facilitator",
        description:
          "Collaborate with medical teams to integrate nutrition into comprehensive cancer care plans.",
      },
      {
        title: "Dietary Side-Effect Management Specialist",
        description:
          "Help patients manage chemotherapy and radiation side effects through specific nutritional interventions.",
      },
      {
        title: "Wellness Educator",
        description:
          "Teach communities and patients about the role of phytochemicals and immune support in oncology.",
      },
      {
        title: "Private Nutrition Coach",
        description:
          "Establish a niche practice focused on personalized nutrition for cancer survivors and patients.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "certificate-herbal-medicine-chronic-diseases",
    title: "Certificate in Herbal Medicine Treatment for Chronic Diseases",
    image: certificate_in_herbal_medicine_treatment_for_chronic_diseases,
    category: "certificate",
    seo: {
      metaTitle:
        "Certificate in Herbal Medicine Treatment for Chronic Diseases | OICNM",
      metaDescription:
        "Master herbal pharmacology and formulation for managing chronic conditions. A 16-week intensive certificate program at OICNM.",
    },
    overview: {
      description:
        "The Herbal Medicine Treatment for Chronic Diseases Programme at OICNM offers comprehensive training in utilizing herbal remedies for managing chronic health conditions. Rooted in holistic healing, this programme equips students with the knowledge and skills to provide effective and natural treatments for individuals dealing with chronic diseases.",
      whoIsItFor: [
        "Aspiring herbalists",
        "Naturopathic practitioners",
        "Healthcare professionals",
        "Individuals seeking alternative approaches to chronic disease management",
      ],
      duration: {
        length: 16,
        unit: "weeks",
        note: "Encompassing coursework, practical training, and clinical experience.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "The programme comprises an extensive curriculum that covers the principles of herbal medicine and its application in chronic disease management through classroom instruction, herbal preparations, case studies, and clinical rotations.",
      courses: [
        "Foundations of Herbal Medicine",
        "Herbal Pharmacology and Therapeutics",
        "Herbal Formulation and Dosage",
        "Herbal Approaches to Cardiovascular Health",
        "Herbal Protocols for Immune System Support",
        "Herbal Strategies for Digestive Health",
        "Case Studies in Herbal Treatment for Chronic Diseases",
      ],

      methodsOfInstruction: [
        "Lectures",
        "Practical workshops",
        "Herbal preparation sessions",
        "Clinical rotations",
        "Hands-on formulation experience",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form along with required documents",
        "Admissions interview",
        "Letters of recommendation",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Specialized Skills Certification",
          status: "Recognised",
        },
      ],
      note: "The Herbal Medicine Treatment for Chronic Diseases Programme offered by OICNM equips graduates with specialized skills in herbal medicine.",
    },
    learningOutcomes: [
      "Understand the principles of herbal medicine and its role in chronic disease management",
      "Analyse individual health conditions and develop personalized herbal treatment plans",
      "Formulate and prepare herbal remedies for specific chronic health challenges",
      "Apply evidence-based herbal protocols for various chronic diseases",
      "Provide guidance on incorporating herbal treatments into holistic wellness plans",
      "Collaborate with healthcare professionals to integrate herbal treatments",
    ],
    careerPaths: [
      {
        title: "Clinical Herbalist",
        description:
          "Consult with individuals to manage chronic health conditions using tailored herbal protocols.",
      },
      {
        title: "Herbal Formula Consultant",
        description:
          "Develop and advise on herbal formulations and dosages for specific health challenges.",
      },
      {
        title: "Integrative Health Practitioner",
        description:
          "Incorporate herbal strategies into existing naturopathic or healthcare practices.",
      },
      {
        title: "Herbal Wellness Educator",
        description:
          "Teach others how to harness the healing power of herbs for chronic disease management.",
      },
      {
        title: "Herbal Product Entrepreneur",
        description:
          "Create and market natural herbal solutions for targeted chronic health support.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "certificate-herbal-medicine-autism-adhd",
    title: "Certificate in Herbal Medicine Protocol for Autism and ADHD",
    image: cet_in_herbal_med_protocol_for_autism,
    category: "certificate",
    seo: {
      metaTitle:
        "Certificate in Herbal Medicine Protocol for Autism and ADHD | OICNM",
      metaDescription:
        "Specialized 12-week training in natural herbal protocols for ASD and ADHD support. Learn holistic behavior management and cognitive support at OICNM.",
    },
    overview: {
      description:
        "The Herbal Medicine Treatment for Autism and ADHD Programme at OICNM offers specialized training in using herbal remedies to support individuals with autism spectrum disorder (ASD) and attention-deficit/hyperactivity disorder (ADHD). Rooted in holistic healing, this programme equips students with the knowledge and skills to provide natural and supportive treatments for individuals with autism and ADHD.",
      whoIsItFor: [
        "Aspiring naturopathic practitioners",
        "Healthcare professionals",
        "Parents and caregivers",
        "Individuals seeking holistic strategies for autism and ADHD support",
      ],
      duration: {
        length: 12,
        unit: "weeks",
        note: "Encompassing coursework, practical training, and clinical experience.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "The programme comprises a comprehensive curriculum that covers the principles of herbal medicine and its application in supporting individuals with autism and ADHD through classroom instruction, hands-on herbal preparations, case studies, and clinical rotations.",
      courses: [
        "Understanding Autism Spectrum Disorder and ADHD",
        "Herbal Remedies for Anxiety and Sensory Challenges",
        "Nutritional Support for Cognitive Function",
        "Herbal Approaches to Behaviour Management",
        "Integrating Herbal Therapies into Holistic Treatment Plans",
        "Case Studies in Herbal Treatment for Autism and ADHD",
      ],

      methodsOfInstruction: [
        "Lectures",
        "Practical workshops",
        "Herbal preparations",
        "Clinical rotations",
        "Hands-on formulation",
        "Case analysis",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form and required documents",
        "Demonstrated interest in herbal medicine and holistic support for autism and ADHD",
        "Letters of recommendation",
        "Admissions interview",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Licensed & Recognized Program",
          status: "Duly Licensed",
        },
      ],
      note: "The Herbal Medicine Treatment for Autism and ADHD Programme offered by OICNM is duly licenced and recognised.",
    },
    learningOutcomes: [
      "Understand the challenges and characteristics of autism and ADHD",
      "Analyse individual cases and develop personalized herbal treatment plans",
      "Formulate herbal remedies to address anxiety, sensory challenges, and cognitive function",
      "Apply evidence-based herbal protocols for behaviour management",
      "Integrate herbal therapies into holistic treatment plans for autism and ADHD",
      "Collaborate with healthcare professionals, parents, and caregivers to provide support",
    ],
    careerPaths: [
      {
        title: "Holistic ASD/ADHD Consultant",
        description:
          "Provide specialized herbal and natural support protocols for individuals with neurodivergent needs.",
      },
      {
        title: "Naturopathic Support Specialist",
        description:
          "Work in integrative clinics focusing on pediatric or behavioral health through natural modalities.",
      },
      {
        title: "Herbal Care Coordinator",
        description:
          "Collaborate with schools and families to integrate natural wellness plans into daily behavior management.",
      },
      {
        title: "Community Health Educator",
        description:
          "Lead workshops for parents and caregivers on safe and effective herbal remedies for sensory challenges.",
      },
      {
        title: "Private Wellness Practitioner",
        description:
          "Establish a specialized practice helping families navigate holistic options for neurodevelopmental support.",
      },
    ],
    callToAction: {
      primary: "Apply Now",
      secondary: "Inquire About Fees",
    },
  },
  {
    slug: "certificate-in-healthy-living",
    title: "Certificate in Healthy Living",
    image: Certificate_in_Healthy_Living,
    category: "certificate",
    seo: {
      metaTitle: "Certificate in Healthy Living | OICNM",
      metaDescription:
        "A 2-week intensive certificate in holistic wellness. Learn balanced nutrition, stress management, and mindful living at OICNM.",
    },
    overview: {
      description:
        "The Healthy Living Programme at OICNM offers a comprehensive exploration of holistic wellness and healthy lifestyle practices. Rooted in the principles of natural medicine, this programme equips students with the knowledge and tools to guide individuals towards achieving optimal health through balanced nutrition, physical activity, stress management, and mindful living.",
      whoIsItFor: [
        "Aspiring health coaches",
        "Nutrition professionals",
        "Individuals seeking to support others in their journey toward vibrant health",
        "Individuals passionate about promoting holistic well-being",
      ],
      duration: {
        length: 2,
        unit: "weeks",
        note: "Completed within 2 weeks, encompassing coursework and practical training.",
      },
      tuitionFee: {
        information:
          "Please refer to the <a href='/admissions' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Admissions Office</a> or the <a href='/tuition-fees' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>Tuition Fees section</a> for detailed information on fees, <a href='/scholarship' class='text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-4 hover:text-emerald-800 transition-colors'>financial aid, and scholarship opportunities</a>.",
      },
    },
    curriculum: {
      composition:
        "A well-rounded curriculum that covers various aspects of healthy living through classroom learning, hands-on exercises, and experiential activities to gain a comprehensive understanding of holistic wellness.",
      courses: [
        "Principles of Holistic Health",
        "Balanced Nutrition for Optimal Well-being",
        "Mindfulness and Stress Management Techniques",
        "Physical Activity and Exercise for a Healthy Lifestyle",
        "Emotional Well-being and Positive Psychology",
        "Integrating Holistic Practices in Daily Life",
        "Creating Personalized Wellness Plans",
      ],

      methodsOfInstruction: [
        "Lectures",
        "Discussions",
        "Experiential activities",
        "Group exercises",
        "Wellness plan development",
        "Lifestyle integration practice",
      ],
    },
    admissionRequirements: {
      criteria: [
        "Open to everyone with a senior school certificate (high school)",
        "Submission of a completed application form and required documents",
        "Demonstrated interest in holistic health and healthy living practices",
        "Admissions interview",
      ],
      reviewTimeline:
        "10 working/business days for review and screening of application after which admission notification will be sent to successful applicants.",
    },
    recognition: {
      accreditation: [
        {
          body: "OICNM Licensed & Recognized Program",
          status: "Duly Licensed",
        },
      ],
      note: "Check the Accreditation section for detailed information on licensing and recognition.",
    },
    learningOutcomes: [
      "Understand the principles of holistic wellness and their importance in health promotion",
      "Educate and guide individuals in making informed choices for balanced nutrition and healthy eating",
      "Implement mindfulness and stress management techniques for emotional well-being",
      "Develop personalized wellness plans that encompass nutrition, exercise, and holistic practices",
      "Advocate for the integration of holistic wellness in daily life",
      "Demonstrate effective communication and coaching skills to support behaviour change",
    ],
    careerPaths: [
      {
        title: "Health and Wellness Coach",
        description:
          "Guide clients through sustainable lifestyle changes, nutrition, and stress management.",
      },
      {
        title: "Lifestyle Consultant",
        description:
          "Work with individuals or groups to integrate holistic health practices into their daily routines.",
      },
      {
        title: "Corporate Wellness Facilitator",
        description:
          "Lead workshops on mindfulness, balanced nutrition, and physical activity in workplace settings.",
      },
      {
        title: "Community Health Advocate",
        description:
          "Educate local communities on the importance of holistic health and disease prevention.",
      },
      {
        title: "Wellness Content Creator",
        description:
          "Share evidence-based healthy living strategies through blogs, seminars, or social platforms.",
      },
    ],
    callToAction: {
      primary: "Enroll Now",
      secondary: "Inquire About Fees",
    },
  },
];

export function getProgrammesByCategory(category: string) {
  return programmes.filter((p) => p.category === category);
}

export function getProgrammeBySlug(slug: string) {
  return programmes.find((p) => p.slug === slug);
}
